import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

@Component({
  selector: 'app-confirm-popup',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule],
  template: `
    <div class="confirm-dialog-container" [style.width.px]="getDialogWidth()">
      <div class="dialog-header">
        <div class="warning-icon">
          <img src="assets/icons/playground/red_warning_icon.svg" alt="warning" width="24" height="24">
        </div>
        <h3 class="dialog-title">{{ data.title }}</h3>
      </div>

      <div class="dialog-content">
        <p class="dialog-message">{{ data.message }}</p>
      </div>

      <div class="dialog-actions">
        <button type="button" class="cancel-btn" (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button type="button" class="confirm-btn" (click)="onConfirm()">
          {{ data.confirmText || 'Yes' }}
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .confirm-dialog-container {
        background: white;
        border-radius: 8px;
        padding: 20px;
        min-width: 480px;
        max-width: 90vw;
        max-height: 90vh;
        box-shadow:
          0px 8px 10px 0px rgba(0, 0, 0, 0.2),
          0px 16px 24px 0px rgba(0, 0, 0, 0.14),
          0px 6px 30px 0px rgba(0, 0, 0, 0.12);
        overflow: visible;
        box-sizing: border-box;
        word-wrap: break-word;
        overflow-wrap: break-word;
        display: flex;
        flex-direction: column;
      }

      .dialog-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 20px;
        min-width: 0;
      }

      .warning-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .dialog-title {
        margin: 0;
        font-family: 'Lexend', sans-serif;
        font-size: 20px;
        font-weight: 600;
        color: #1e2225;
        line-height: 23px;
        letter-spacing: 0px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex: 1;
      }

      .dialog-content {
        margin-bottom: 20px;
        min-width: 0;
        flex: 1;
        overflow: visible;
      }

      .dialog-message {
        margin: 0;
        font-family: 'Lexend', sans-serif;
        font-size: 14px;
        color: #475258;
        line-height: 24px;
        letter-spacing: -0.16px;
        white-space: pre-line;
        word-wrap: break-word;
        overflow-wrap: break-word;
        min-width: 0;
        max-width: 100%;
        hyphens: auto;
        overflow: visible;
      }

      .dialog-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        width: 100%;
        margin-top: 0;
        min-width: 0;
        flex-shrink: 0;
      }

      .cancel-btn,
      .confirm-btn {
        height: 32px;
        padding: 5px 8px;
        border-radius: 4px;
        font-family: 'Lexend', sans-serif;
        font-size: 14px;
        font-weight: 400;
        cursor: pointer;
        border: none;
        line-height: 24px;
        letter-spacing: -0.16px;
        transition: all 0.2s ease;
        min-width: 60px;
        flex-shrink: 0;
      }

      .cancel-btn {
        background: #ebeff5;
        color: #475258;
      }

      .cancel-btn:hover {
        background: #dde5ed;
      }

      .confirm-btn {
        background: #FF4444;
        color: white;
      }

      .confirm-btn:hover {
        background: #e63939;
      }

      /* Global overlay styles */
      ::ng-deep .cdk-overlay-backdrop {
        background: var(--background-variant-81-e-2225, #1E2225) !important;
        opacity: 0.4 !important;
      }

      ::ng-deep .cdk-overlay-pane {
        overflow: visible !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      ::ng-deep .mat-mdc-dialog-container {
        overflow: visible !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      ::ng-deep .mat-mdc-dialog-surface {
        overflow: visible !important;
        padding: 0 !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      /* Dialog panel specific styles */
      ::ng-deep .confirm-dialog-panel {
        overflow: visible !important;
        max-width: 90vw !important;
        max-height: 90vh !important;
      }

      ::ng-deep .confirm-dialog-backdrop {
        background: var(--background-variant-81-e-2225, #1E2225) !important;
        opacity: 0.4 !important;
      }

      /* Ensure no horizontal scrollbars and proper sizing */
      ::ng-deep .mat-mdc-dialog-container,
      ::ng-deep .mat-mdc-dialog-surface,
      ::ng-deep .cdk-overlay-pane {
        max-width: 90vw !important;
        max-height: 90vh !important;
        overflow: visible !important;
      }
    `,
  ],
})
export class ConfirmPopupComponent {
  private readonly BASE_WIDTH = 480;
  private readonly MIN_WIDTH_PER_CHAR = 8; // Approximate pixels per character
  private readonly MAX_WIDTH = 800; // Maximum width before wrapping
  private readonly PADDING = 40; // Account for padding and margins

  constructor(
    public dialogRef: MatDialogRef<ConfirmPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {}

  getDialogWidth(): number {
    if (!this.data?.message) {
      return this.BASE_WIDTH;
    }

    // Calculate approximate width needed for the message
    // Consider line breaks and average character width
    const messageLength = this.data.message.length;
    const estimatedWidth = messageLength * this.MIN_WIDTH_PER_CHAR + this.PADDING;

    // Use base width for short messages
    if (estimatedWidth <= this.BASE_WIDTH) {
      return this.BASE_WIDTH;
    }

    // For longer messages, increase width but cap at max width
    const calculatedWidth = Math.min(estimatedWidth, this.MAX_WIDTH);

    // Round to nearest 20px for cleaner appearance
    const finalWidth = Math.ceil(calculatedWidth / 20) * 20;

    // Ensure minimum width and reasonable maximum
    return Math.max(this.BASE_WIDTH, Math.min(finalWidth, this.MAX_WIDTH));
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
